import { useRef, useState } from "react";
import { motion, AnimatePresence, useMotionValueEvent, useScroll, useSpring } from "framer-motion";
import { ArrowDown } from "lucide-react";
import pageOne from "@/assets/pdf-pages/page-1.png";
import pageTwo from "@/assets/pdf-pages/page-2.png";
import pageThree from "@/assets/pdf-pages/page-3.png";
import pageFour from "@/assets/pdf-pages/page-4.png";

const pages = [
  { image: pageOne, title: "Packing List", description: "Keep documents, clothes, and essentials organized before campus life begins." },
  { image: pageTwo, title: "Weekly MBA Planner", description: "Plan priorities, academics, habits, and weekly reflection in one view." },
  { image: pageThree, title: "Profile Building Roadmap", description: "Track goals, certifications, internships, networking, and placement readiness." },
  { image: pageFour, title: "Committee Guide", description: "Compare committee roles, benefits, and reality checks before choosing." },
];

export default function PdfScrollShowcase() {
  const sectionRef = useRef(null);
  const [active, setActive] = useState(0);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });
  const smoothProgress = useSpring(scrollYProgress, { stiffness: 180, damping: 24, mass: 0.16 });

  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    const next = Math.min(pages.length - 1, Math.max(0, Math.floor(latest * pages.length)));
    setActive(next);
  });

  const page = pages[active];

  return (
    <section id="pdf-showcase" ref={sectionRef} className="relative h-[250vh] bg-[#073C2F] md:h-[285vh]">
      <div className="sticky top-0 flex min-h-screen items-center overflow-hidden px-4 py-12 text-[#FBF7EF] sm:px-5 md:px-10 md:py-20">
        <motion.div className="absolute left-0 top-0 h-1 origin-left bg-[#D5AD76]" style={{ scaleX: smoothProgress, width: "100%" }} />
        <div className="mx-auto grid w-full max-w-7xl items-center gap-9 lg:grid-cols-[0.82fr_1.18fr]">
          <div className="order-2 lg:order-1">
            <p className="font-body text-sm font-bold uppercase tracking-[0.28em] text-[#D5AD76]">
              Page {String(active + 1).padStart(2, "0")} / {String(pages.length).padStart(2, "0")}
            </p>
            <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#D5AD76]/70 bg-white/10 px-4 py-2 font-body text-sm font-bold text-[#FBF7EF]">
              <ArrowDown size={16} className="text-[#D5AD76]" />
              Scroll to see planner previews
            </div>
            <AnimatePresence mode="wait">
              <motion.div
                key={page.title}
                initial={{ opacity: 0, y: 22 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -18 }}
                transition={{ duration: 0.28, ease: "easeOut" }}
              >
                <h2 className="mt-4 font-heading text-3xl uppercase leading-none sm:text-4xl md:text-6xl">{page.title}</h2>
                <p className="mt-4 max-w-md font-body text-base leading-7 text-[#FBF7EF]/78 md:mt-5 md:text-lg md:leading-8">{page.description}</p>
              </motion.div>
            </AnimatePresence>
            <div className="mt-8 flex gap-2">
              {pages.map((item, index) => (
                <span
                  key={item.title}
                  className={`h-2 rounded-full transition-all ${index === active ? "w-12 bg-[#D5AD76]" : "w-2 bg-[#FBF7EF]/35"}`}
                />
              ))}
            </div>
          </div>

          <div className="order-1 flex justify-center lg:order-2" style={{ perspective: 1200 }}>
            <AnimatePresence mode="popLayout">
              <motion.div
                key={page.title}
                className="relative w-full max-w-[320px] rounded-2xl bg-[#FBF7EF] p-2 shadow-[0_34px_110px_rgba(0,0,0,0.36)] sm:max-w-[420px] md:max-w-[520px] md:p-3"
                initial={{ opacity: 0, x: 70, scale: 0.92, rotateY: -14, rotateZ: 3 }}
                animate={{ opacity: 1, x: 0, scale: 1, rotateY: 0, rotateZ: active % 2 ? -1.2 : 1.2 }}
                exit={{ opacity: 0, x: -70, scale: 0.93, rotateY: 14, rotateZ: -3 }}
                transition={{ duration: 0.34, ease: [0.22, 1, 0.36, 1] }}
              >
                {/* Replace these PNG files in src/assets/pdf-pages/ with exported planner page screenshots. */}
                <img
                  src={page.image}
                  alt={`${page.title} planner page preview`}
                  className="aspect-[4/5.25] w-full rounded-xl object-cover"
                  loading={active < 2 ? "eager" : "lazy"}
                />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
